import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaTimes } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const LEVELS = ["A1", "A2", "B1", "B2", "AP", "B1_Exam_Preparation"];
const CHAPTERS = Array.from({ length: 14 }, (_, i) => `Chapter ${i + 1}`);

type Question = {
  id: number;
  questionText: string;
  options: string[] | string;
  correctAnswer: string;
  explanation?: string;
};

const defaultQuestion = {
  id: 0,
  questionText: "",
  options: ["", "", "", ""],
  correctAnswer: "",
  explanation: "",
};

export default function CreateQuizPage() {
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState({
    title: "",
    chapter: "",
    level: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quizId, setQuizId] = useState<number | null>(null);

  // Question modal state
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formQuestion, setFormQuestion] = useState<Question>(defaultQuestion);
  const [formError, setFormError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setQuiz((prev) => ({ ...prev, [name]: value }));

    if (name === "chapter" || name === "level") {
      const chapter = name === "chapter" ? value : quiz.chapter;
      const level = name === "level" ? value : quiz.level;

      if (chapter && level) {
        setQuiz((prev) => ({
          ...prev,
          title: `${chapter} - ${level.replace("_", " ")}`,
          [name]: value,
        }));
      }
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/quizzes/mentor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(quiz),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Failed to create quiz");
      }

      const data = await res.json();
      setQuizId(data.id);
    } catch (err: any) {
      setError(err.message || "Failed to create quiz");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/manage-quizzes");
  };

  // Open question modal and fetch questions
  const handleOpenQuestionModal = async () => {
    if (!quizId) return;
    setShowQuestionModal(true);
    setQuestionsLoading(true);
    setIsAdding(false);
    setEditingQuestion(null);
    setFormError("");

    try {
      const res = await fetch(
        `${API_BASE}/api/quizzes/mentor/${quizId}/questions`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to fetch questions");
      const data = await res.json();
      setQuizQuestions(Array.isArray(data) ? data : []);
    } catch (err) {
      setQuizQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleCloseQuestionModal = () => {
    setShowQuestionModal(false);
    setQuizQuestions([]);
    setEditingQuestion(null);
    setIsAdding(false);
    setFormError("");
  };

  // Add Question
  const handleAddQuestion = () => {
    setIsAdding(true);
    setEditingQuestion(null);
    setFormQuestion({ ...defaultQuestion });
    setFormError("");
  };

  // Edit Question
  const handleEditQuestion = (q: Question) => {
    setEditingQuestion(q);
    setIsAdding(false);
    setFormQuestion({
      ...q,
      options: Array.isArray(q.options)
        ? [...q.options]
        : JSON.parse(q.options || "[]"),
    });
    setFormError("");
  };

  // Delete Question
  const handleDeleteQuestion = async (questionId: number) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/quizzes/mentor/questions/${questionId}`,
        { method: "DELETE", credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to delete question");
      setQuizQuestions((prev) => prev.filter((q) => q.id !== questionId));
    } catch (err) {
      alert("Failed to delete question.");
    }
  };

  // Form Handlers
  const handleFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
    idx?: number
  ) => {
    const { name, value } = e.target;
    if (name === "options" && typeof idx === "number") {
      setFormQuestion((q) => {
        const newOpts = Array.isArray(q.options) ? [...q.options] : [];
        newOpts[idx] = value;
        return { ...q, options: newOpts };
      });
    } else {
      setFormQuestion((q) => ({ ...q, [name]: value }));
    }
  };

  // Submit Edit or Add
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!quizId) return;

    const payload = {
      questionText: formQuestion.questionText,
      options: Array.isArray(formQuestion.options)
        ? formQuestion.options
        : JSON.parse(formQuestion.options || "[]"),
      correctAnswer: formQuestion.correctAnswer,
      explanation: formQuestion.explanation,
    };

    try {
      let res, data: Question;
      if (editingQuestion) {
        // Edit
        res = await fetch(
          `${API_BASE}/api/quizzes/mentor/questions/${editingQuestion.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
          }
        );
        if (!res.ok) throw new Error("Failed to update question");
        data = await res.json();
        setQuizQuestions((prev) =>
          prev.map((q) => (q.id === editingQuestion.id ? { ...q, ...data } : q))
        );
        setEditingQuestion(null);
      } else if (isAdding) {
        // Add
        res = await fetch(
          `${API_BASE}/api/quizzes/mentor/${quizId}/questions`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(payload),
          }
        );
        if (!res.ok) throw new Error("Failed to add question");
        data = await res.json();
        setQuizQuestions((prev) => [...prev, data]);
        setIsAdding(false);
      }
      setFormQuestion({ ...defaultQuestion });
    } catch (err: any) {
      setFormError(err.message || "Error");
    }
  };

  // Cancel edit/add
  const handleCancelForm = () => {
    setEditingQuestion(null);
    setIsAdding(false);
    setFormQuestion({ ...defaultQuestion });
    setFormError("");
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaPlus />}
        title="Create New Quiz"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Quizzes", to: "/admin/manage-quizzes" },
          { label: "Create Quiz" },
        ]}
      />

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {quizId ? (
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Quiz Created Successfully!
              </h3>
              <p className="text-gray-600 mb-6">
                Your quiz has been created. Now you can add questions to it.
              </p>

              {/* Quiz Info */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="space-y-2 text-left">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Quiz ID:</span>
                    <span className="font-semibold text-[#009fe3]">
                      {quizId}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Title:</span>
                    <span className="font-semibold text-[#009fe3]">
                      {quiz.title}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Level:</span>
                    <span className="font-semibold text-[#009fe3]">
                      {quiz.level.replace("_", " ")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Chapter:</span>
                    <span className="font-semibold text-[#009fe3]">
                      {quiz.chapter}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleOpenQuestionModal}
                className="flex-1 bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-colors flex items-center justify-center gap-2"
              >
                <FaPlus />
                Add Questions to This Quiz
              </button>
              <button
                onClick={() => {
                  setQuizId(null);
                  setQuiz({ title: "", chapter: "", level: "" });
                }}
                className="flex-1 bg-green-100 hover:bg-green-200 text-green-700 py-3 rounded-lg font-semibold shadow-md transition-colors"
              >
                Create Another Quiz
              </button>
            </div>

            <div className="mt-4 text-center">
              <button
                onClick={() => navigate("/admin/manage-quizzes")}
                className="text-gray-600 hover:text-gray-800 text-sm underline"
              >
                Back to Manage Quizzes
              </button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={handleCreate}
            className="bg-white rounded-xl shadow-lg p-6 sm:p-8"
          >
            {error && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                <p className="font-semibold">Error</p>
                <p>{error}</p>
              </div>
            )}

            <div className="space-y-6 mb-8">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Level <span className="text-red-500">*</span>
                </label>
                <select
                  name="level"
                  value={quiz.level}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                >
                  <option value="">Select Level First</option>
                  {LEVELS.map((lvl) => (
                    <option key={lvl} value={lvl}>
                      {lvl.replace("_", " ")}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select the level first, then choose a chapter
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chapter <span className="text-red-500">*</span>
                </label>
                <select
                  name="chapter"
                  value={quiz.chapter}
                  onChange={handleChange}
                  required
                  disabled={!quiz.level}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  style={{ fontSize: "16px" }}
                >
                  <option value="">
                    {quiz.level ? "Select Chapter" : "Select Level First"}
                  </option>
                  {CHAPTERS.map((chapter) => (
                    <option key={chapter} value={chapter}>
                      {chapter}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  All levels have 14 chapters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quiz Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={quiz.title}
                  onChange={handleChange}
                  placeholder="e.g. Chapter 1 - A1"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  style={{ fontSize: "16px" }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Auto-generated from chapter and level, but you can edit it
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <FaPlus />
                    Create Quiz
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold shadow-md transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <FaTimes />
                Cancel
              </button>
            </div>
          </form>
        )}

        {!quizId && quiz.level && quiz.chapter && (
          <div className="mt-6 bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 shadow-lg">
            <h3 className="text-sm font-semibold text-gray-600 mb-3">
              📋 Preview
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Level:</span>
                <span className="font-semibold text-[#009fe3]">
                  {quiz.level.replace("_", " ")}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Chapter:</span>
                <span className="font-semibold text-[#009fe3]">
                  {quiz.chapter}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Title:</span>
                <span className="font-semibold text-[#009fe3]">
                  {quiz.title}
                </span>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Question Modal */}
      {showQuestionModal && quizId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
          onClick={handleCloseQuestionModal}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-auto p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseQuestionModal}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 text-3xl font-bold"
              aria-label="Close"
            >
              &times;
            </button>
            <h3 className="text-2xl font-bold mb-6 text-[#009fe3]">
              Add Questions to Quiz
            </h3>

            {questionsLoading ? (
              <div className="text-center text-[#009fe3]">Loading...</div>
            ) : (
              <>
                {/* Edit/Add Form */}
                {(editingQuestion || isAdding) && (
                  <form
                    onSubmit={handleFormSubmit}
                    className="mb-6 bg-gray-50 p-4 rounded-xl shadow"
                  >
                    <div className="mb-3">
                      <label className="block font-semibold mb-1">
                        Question Text
                      </label>
                      <input
                        type="text"
                        name="questionText"
                        value={formQuestion.questionText}
                        onChange={handleFormChange}
                        required
                        className="w-full border p-2 rounded"
                        style={{ fontSize: "16px" }}
                      />
                    </div>
                    {[0, 1, 2, 3].map((idx) => (
                      <div className="mb-3" key={idx}>
                        <label className="block text-sm mb-1">
                          Option {idx + 1}
                        </label>
                        <input
                          type="text"
                          name="options"
                          value={formQuestion.options[idx] || ""}
                          onChange={(e) => handleFormChange(e, idx)}
                          required
                          className="w-full border p-2 rounded"
                          style={{ fontSize: "16px" }}
                        />
                      </div>
                    ))}
                    <div className="mb-3">
                      <label className="block font-semibold mb-1">
                        Correct Answer
                      </label>
                      <select
                        name="correctAnswer"
                        value={formQuestion.correctAnswer}
                        onChange={handleFormChange}
                        required
                        className="w-full border p-2 rounded"
                        style={{ fontSize: "16px" }}
                      >
                        <option value="">Select Correct Answer</option>
                        {(Array.isArray(formQuestion.options)
                          ? formQuestion.options
                          : JSON.parse(formQuestion.options || "[]")
                        ).map((opt: string, idx: number) => (
                          <option key={idx} value={opt}>
                            {opt || `Option ${idx + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="block font-semibold mb-1">
                        Explanation (optional)
                      </label>
                      <textarea
                        name="explanation"
                        value={formQuestion.explanation}
                        onChange={handleFormChange}
                        className="w-full border p-2 rounded"
                        style={{ fontSize: "16px" }}
                      />
                    </div>
                    {formError && (
                      <div className="text-red-500 mb-2">{formError}</div>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        className="bg-[#009fe3] hover:bg-[#007cb5] text-white px-5 py-2 rounded-lg font-semibold shadow"
                      >
                        {editingQuestion ? "Save Changes" : "Add Question"}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelForm}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-5 py-2 rounded-lg font-semibold shadow"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Questions List */}
                <ol className="space-y-6 list-decimal list-inside">
                  {quizQuestions.map((q) => (
                    <li
                      key={q.id}
                      className="border p-4 rounded-lg shadow-sm bg-gray-50"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-lg">
                            {q.questionText}
                          </div>
                          <ul className="ml-6 mt-2 list-disc text-sm text-gray-700">
                            {(Array.isArray(q.options)
                              ? q.options
                              : JSON.parse(q.options || "[]")
                            ).map((opt: string, i: number) => (
                              <li key={i}>{opt}</li>
                            ))}
                          </ul>
                          <div className="text-green-700 text-xs mt-1">
                            Correct: {q.correctAnswer}
                          </div>
                          {q.explanation && (
                            <div className="mt-2 text-gray-600 text-sm italic">
                              Explanation: {q.explanation}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          <button
                            className="text-blue-600 hover:underline"
                            onClick={() => handleEditQuestion(q)}
                          >
                            Edit
                          </button>
                          <button
                            className="text-red-600 hover:underline"
                            onClick={() => handleDeleteQuestion(q.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>

                {/* Add Question Button */}
                {!isAdding && !editingQuestion && (
                  <div className="mt-6">
                    <button
                      className="bg-[#009fe3] hover:bg-[#007cb5] text-white px-5 py-2 rounded-lg font-semibold shadow"
                      onClick={handleAddQuestion}
                    >
                      + Add New Question
                    </button>
                  </div>
                )}

                {quizQuestions.length === 0 && (
                  <div className="text-center text-gray-400 py-10">
                    No questions found. Click "Add New Question" to get started!
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
